import { isAuthenticatedUser } from 'js/lib/user';

import { useMarkAddCareerGoalAsComplete } from 'bundles/daily-goals/hooks/useMarkAddCareerGoalAsComplete';
import { areDailyGoalsEnabled } from 'bundles/daily-goals/utils/epicFeatureFlags';
import { QUESTION_IDS } from 'bundles/onboarding-common/constants/questionScheme';
import { useOnboardingQuestionData } from 'bundles/onboarding-common/contexts/OnboardingQuestionDataContext';
import useGetLearnerObjectPromise from 'bundles/onboarding-common/hooks/useGetLearnerObjectPromise';
import useUpdateDesiredSkillsOrUnsure from 'bundles/onboarding-common/hooks/useUpdateDesiredSkillsOrUnsure';
import { updateLocalStorageOnboardingData } from 'bundles/onboarding-common/utils/localStorageUtils';

const useSubmitAnswer = () => {
  const { updateDesiredSkillsOrUnsure } = useUpdateDesiredSkillsOrUnsure();
  const { questionValue, setQuestionValue, submitAnswerOptions } = useOnboardingQuestionData();
  const { getLearnerObjectPromise } = useGetLearnerObjectPromise();
  // will only mark the daily goals career goal as complete if in the daily goals epic
  const { markAddCareerGoalAsComplete } = useMarkAddCareerGoalAsComplete();
  const dailyGoalsEnabled = areDailyGoalsEnabled();

  const isUnregisteredUser = !isAuthenticatedUser();
  const shouldClearSavedSkills = submitAnswerOptions?.shouldClearSavedSkills;

  const promise = async () => {
    if (isUnregisteredUser) {
      if (!questionValue) {
        return Promise.resolve();
      }
      const clearSavedSkills = shouldClearSavedSkills
        ? { [QUESTION_IDS.skillSelect]: { __typename: QUESTION_IDS.skillSelect, value: [] } }
        : {};
      return updateLocalStorageOnboardingData({ [questionValue?.__typename]: questionValue, ...clearSavedSkills });
    }

    if (shouldClearSavedSkills) {
      await updateDesiredSkillsOrUnsure(null, true);
    }

    const learnerObjectPromiseUpdateResult = await getLearnerObjectPromise({ questionValue });

    // Note: this if block will only be reached if the mutation to update the learner object succeeds
    if (questionValue?.__typename === QUESTION_IDS.learningGoal && dailyGoalsEnabled) {
      markAddCareerGoalAsComplete();
    }
    return learnerObjectPromiseUpdateResult;
  };

  const submitAnswer = async () => {
    if (questionValue) {
      try {
        await promise();
        setQuestionValue(null);

        return Promise.resolve();
      } catch {
        return Promise.reject();
      }
    }

    return Promise.resolve();
  };

  return { submitAnswer };
};

export default useSubmitAnswer;
